import { OidcProvider, useOidc } from "@axa-fr/react-oidc";
import { FC, PropsWithChildren, useMemo } from "react";
import { OidcAuthMessage, OidcContext, OidcPath } from "../types/oidcTypes";
import { getOidcConfigurationForContext } from "../utils/oidcConfiguration";
import { getOrCreateOidcClient } from "../utils/oidcUtils";

export interface AuthContextProviderProps {
  oidcContext: OidcContext;
}

export const AuthContextProvider: FC<PropsWithChildren<AuthContextProviderProps>> = ({ children, oidcContext }) => {
  const configuration = useMemo(() => getOidcConfigurationForContext(oidcContext), [oidcContext]);
  // create the `OidcClient` (which is dependent on the current context) to initialize it before the `useOidc` hook
  const oidcClient = useMemo(() => getOrCreateOidcClient(oidcContext), [oidcContext]);
  const { isAuthenticated } = useOidc(oidcContext);

  // login using the normal flow, when we are on the correct page and not authenticated yet
  if (location.pathname.endsWith(OidcPath.LOGIN) && !isAuthenticated) {
    oidcClient.loginAsync();
  }

  // notify our opening window of our successful authentication
  if (window.opener != null && isAuthenticated) {
    sendPostMessage(isAuthenticated);
  }

  return (
    <OidcProvider
      authenticatingErrorComponent={CallbackError}
      configurationName={oidcContext}
      configuration={configuration}
    >
      {children}
    </OidcProvider>
  );
};

interface OidcComponentProps {
  configurationName: string;
}

const CallbackError: FC<OidcComponentProps> = ({ configurationName }) => {
  // notify our opening window of our unsuccessful authentication
  usePostMessage(configurationName as OidcContext);
  return null;
};

const usePostMessage = (oidcContext: OidcContext) => {
  const { isAuthenticated } = useOidc(oidcContext);
  sendPostMessage(isAuthenticated);
};

const sendPostMessage = (isAuthenticated: boolean) => {
  const message: OidcAuthMessage = { isAuthenticated, type: "AUTH" };
  console.log("Posting message:", message);
  window.opener?.postMessage(message);
};
